import React, { useEffect, useState, useRef } from "react";
import {
  ArrowPathIcon,
  TrashIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import { formatDate } from "../../utils/helps";
import CustomTableComponent from "../../components/CustomTableComponent";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import { createPortal } from "react-dom";

const companyId = localStorage.getItem("companyId");

const StatusDropdown = ({
  options = [],
  value,
  onChange,
  minWidth = "w-28",
  getStatusStyle = () => "bg-gray-100 text-gray-800",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const selectedOption = options.find((opt) => opt.value === value);

  const handleToggle = () => {
    if (!disabled) setIsOpen((open) => !open);
  };

  const handleSelect = (optionValue) => {
    if (disabled) return;
    setIsOpen(false);
    if (typeof onChange === "function") onChange(optionValue);
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      const inButton =
        buttonRef.current && buttonRef.current.contains(e.target);
      const inDropdown =
        dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inButton && !inDropdown) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const dropdown = (
    <div
      ref={dropdownRef}
      className="z-[99999] bg-white rounded-lg shadow-lg border border-gray-200"
      style={{
        position: "fixed",
        top: dropdownPos.top,
        left: dropdownPos.left,
        minWidth: dropdownPos.width,
      }}
    >
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => handleSelect(option.value)}
          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
            value === option.value ? "bg-gray-100 font-semibold" : ""
          }`}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`flex justify-between items-center space-x-1 px-3 py-1 rounded-full text-sm whitespace-nowrap ${minWidth} ${getStatusStyle(
          value,
        )} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        style={{ zIndex: 10 }}
      >
        <span>{selectedOption?.label || "Chưa chọn"}</span>
        <ChevronDownIcon className="w-4 h-4" />
      </button>
      {isOpen && !disabled && createPortal(dropdown, document.body)}
    </>
  );
};

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};
const hasPermission = (slug) => {
  const userId = localStorage.getItem("userId");
  if (userId == 1) return true;
  return getUserPermissions().includes(slug);
};

const ContactListFeature = () => {
  const [contacts, setContacts] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setStatus("loading");
    setError("");
    try {
      if (hasPermission("CONTACT_SHOW")) {
        const res = await ApiCustom.get("/contact-show-by-company-id/" + companyId);
        setContacts(res.data?.data || []);
      } else {
        setContacts([]);
      }
      setStatus("succeeded");
    } catch {
      setContacts([]);
      setStatus("failed");
      setError("Không thể tải dữ liệu liên hệ!");
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await ApiCustom.put(`/contact-update/${contactId}`, {
        status: newStatus,
      });
      toast.success("Cập nhật trạng thái thành công!");
      fetchContacts();
    } catch {
      toast.error("Đã xảy ra lỗi khi cập nhật trạng thái!");
    }
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };
  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
  };
  const handleDeleteContact = async () => {
    try {
      await ApiCustom.delete("/contact-delete/" + deleteId);
      toast.success("Xóa liên hệ thành công");
      handleCloseDeleteModal();
      fetchContacts();
    } catch {
      toast.error("Có lỗi xảy ra khi xóa liên hệ");
    }
  };

  const headers = [
    {
      key: "id",
      label: "STT",
      render: (item, index) => <span className="font-medium">{index + 1}</span>,
    },
    {
      key: "name",
      label: "Họ và tên",
      render: (item) => <span className="font-medium">{item.name}</span>,
    },
    {
      key: "email",
      label: "Email",
      render: (item) => <span className="font-medium">{item.email}</span>,
    },
    {
      key: "phone",
      label: "Số điện thoại",
      render: (item) => <span className="font-medium">{item.phone}</span>,
    },
    {
      key: "product",
      label: "Sản phẩm",
      render: (item) => (
        <span className="font-medium">{item?.product?.name}</span>
      ),
    },
    {
      key: "createdAt",
      label: "Thời gian",
      render: (item) => (
        <span className="font-medium">{formatDate(item?.createdAt)}</span>
      ),
    },
  ];

  if (hasPermission("CONTACT_UPDATE")) {
    headers.push({
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <StatusDropdown
          key={`${item.id}_${item.status}`}
          options={[
            { value: "pending", label: "Đang chờ" },
            { value: "received", label: "Đã tiếp nhận" },
            { value: "processed", label: "Đã xử lý" },
          ]}
          value={item.status}
          onChange={(newStatus) => handleStatusChange(item.id, newStatus)}
          minWidth="w-32"
          getStatusStyle={(value) => {
            switch (value) {
              case "processed":
                return "bg-green-100 text-green-800";
              case "received":
                return "bg-blue-100 text-blue-800";
              default:
                return "bg-yellow-100 text-yellow-800";
            }
          }}
        />
      ),
    });
  }

  if (hasPermission("CONTACT_DELETE")) {
    headers.push({
      key: "setting",
      label: "Thao tác",
      render: (item) => (
        <button
          onClick={() => handleOpenDeleteModal(item.id)}
          className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      ),
    });
  }

  const filterData = contacts.filter((item) =>
    searchValue === ""
      ? true
      : [item?.name, item?.email, item?.phone]
          .filter(Boolean)
          .some((field) =>
            field?.toLowerCase().includes(searchValue.toLowerCase()),
          ),
  );

  return (
    <div className="p-6 bg-white rounded-2xl shadow-md border mb-40">
      <h2 className="text-2xl font-bold text-emerald-700 mb-1">
        Quản lý liên hệ đặt hàng
      </h2>
      <p className="text-gray-500 text-sm mb-3">
        Danh sách liên hệ gửi về hệ thống
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <label className="text-sm font-medium w-24">Tìm kiếm:</label>
        <input
          type="search"
          className="w-full max-w-xs px-2 py-1 border rounded-lg focus:outline-none pr-10"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>
      <div className="rounded-lg shadow">
        {status === "loading" ? (
          <div className="flex justify-center items-center h-52">
            <ArrowPathIcon className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : status === "failed" ? (
          <div className="p-4 text-red-500 bg-red-50 rounded-md">
            Đã xảy ra lỗi: {error}
          </div>
        ) : (
          <CustomTableComponent headers={headers} data={filterData} />
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteContact}
      />
    </div>
  );
};

export default ContactListFeature;
